import {resource} from '@albert-io/json-api-framework/request/builder';
import sessionStore from 'client/Session/SessionStore';
import {set} from 'lodash';
import {applyFilters} from 'components/PracticeView/PracticeViewToolbar/Filters/filters.utils';
import {applySort} from 'components/PracticeView/PracticeViewToolbar/Sort/sort.utils';
import masqueradeStore from 'generic/Masquerade/Masquerade.store';
import {withSuppressedAccessToasts} from 'lib/accessToastSuppression';

export function getFolderQuery(folderId) {
  return resource('template_v1')
    .mandarkEndpoint(['templates_v1', folderId])
    .include('question_sets_v1.questions_v3')
    .include('question_sets_v1.subject_v2')
    .withMeta('template_v1,subject_v2');
}

// A cached errored interest resolves (not rejects) on remount, wedging the view; refetch instead
function invalidateWedgedInterest(query) {
  if (!query.isResourceValid()) {
    query.invalidateInterest();
  }
  return query;
}

export async function getFolder(folderId) {
  return withSuppressedAccessToasts(() =>
    invalidateWedgedInterest(getFolderQuery(folderId)).getResourcePromise()
  );
}

export function getFolderQuestionSetsQuery(
  folderId,
  pageSize = 20,
  questionSetIds = [],
  {withCalculatorTier = false} = {}
) {
  const metaContext = {
    meta: {
      context: {
        template: {
          id: folderId
        },
        user: {
          personalization_settings: true
        }
      }
    }
  };

  const userId = masqueradeStore.getUserIdByMasqueradeState();
  if (sessionStore.hasTeacherAccess()) {
    set(metaContext, 'meta.context.user.role', 'teacher');
  }

  if (userId) {
    set(metaContext, 'meta.context.user.id', userId);
  } else {
    set(metaContext, 'meta.context.no_user', true);
  }

  if (withCalculatorTier) {
    set(metaContext, 'meta.context.effective_calculator_type', true);
  }

  let query = resource('question_sets_v1')
    .mandarkEndpoint(['templates_v1', folderId, 'question_sets_v1'])
    .filter(
      {
        id: questionSetIds.join(',')
      },
      questionSetIds.length > 0
    )
    .pageSize(pageSize)
    .include('guide_levels_v2.guide_v1')
    .fields({
      guide_v1: 'guide_type'
    })
    .include(withCalculatorTier ? 'subject_v2.curriculum_area_v1' : 'subject_v2')
    .include('questions_v3')
    .include('questions_v3.question_set_v1.tags_v1')
    .include('questions_v3.question_set_v1.subject_v2')
    .include('questions_v3.standards_v1')
    .include('questions_v3.standards_v1.standard_set_v1')
    .withMeta('question_v3,subject_v2,question_set_v1,standard_v1')
    .customQuery(metaContext, Object.keys(metaContext).length !== 0);

  query = applyFilters(applySort(query, 'meta.assignment_position'));
  return query;
}

/** The folder practice view's question sets: the shared query plus what its calculator reads. */
export function getFolderPracticeQuestionSetsQuery(folderId) {
  return getFolderQuestionSetsQuery(folderId, undefined, undefined, {withCalculatorTier: true});
}

export async function getFolderQuestionSets(folderId) {
  const questionSets = await withSuppressedAccessToasts(() =>
    invalidateWedgedInterest(getFolderPracticeQuestionSetsQuery(folderId)).getResourcePromise()
  );
  return questionSets;
}
